import { useState, useEffect, createContext, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import { toast } from 'react-toastify';

import {
  fetchBrand,
  fetchBrandConfigModulesPack,
  fetchPaymentTypes,
  updateBrandConfigModulesPack,
} from '../../../actions/Brands';
import { get, put } from '../../../helpers/apiHelpers';

const DYNAMIC_BACKGROUND = {
  defaultCatalog: '/media-catalogs/3ba7ea4c-736c-46b2-bf02-f2401e8a41c7',
};

const DISH_IMAGE_PLACEHOLDER = {
  PLImageCatalog: '/media-catalogs/860c7911-9873-4705-afa6-939d03c1dd4c',
  PLTransparentImageCatalog:
    '/media-catalogs/bd7b8341-80bf-42ec-8890-197e827d57b1',
  CZImageCatalog: '/media-catalogs/d77dcced-eade-4c78-a01d-80820511078a',
  CZTransparentImageCatalog:
    '/media-catalogs/2a24e245-6f90-4831-bba7-d37b7aca05c6',
};

// eslint-disable-next-line @typescript-eslint/no-empty-function -- FIXME
const PanelConfigurationContext = createContext([{}, () => {}]);

const PanelConfigurationContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const selectedBrand = useSelector(state => state.Auth.selectedBrand);
  const paymentTypes = useSelector(state => state.Brands.paymentTypes);
  const selectedLanguage = useSelector(
    state => state.Brands.brand.multinational.defaultLanguage
  );

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [backgroundDialogMode, setBackgroundDialogMode] = useState(false);

  const [choicesBackground, setChoicesBackground] = useState({
    image: {},
    index: null,
  });
  const [backgroundToChoicedArray, setBackgroundToChoicedArray] = useState([]);

  const [choicesAlternatePhotoDish, setChoicesAlternatePhotoDish] = useState({
    image: {},
    index: null,
  });
  const [alternatePhotoToDishArray, setAlternatePhotoToDishArray] = useState(
    []
  );

  const catalogTransparentRelativeToLanguage = () => {
    if (
      selectedLanguage.toLowerCase() === 'cz' ||
      selectedLanguage.toLowerCase() === 'cs'
    ) {
      return DISH_IMAGE_PLACEHOLDER.CZTransparentImageCatalog;
    }
    return DISH_IMAGE_PLACEHOLDER.PLTransparentImageCatalog;
  };

  useEffect(() => {
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    get(DYNAMIC_BACKGROUND.defaultCatalog).then(response => {
      setBackgroundToChoicedArray(response.contents);
    });

    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    get(catalogTransparentRelativeToLanguage()).then(response => {
      setAlternatePhotoToDishArray(response.contents);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const [brandConfig, setBrandConfig] = useState({});
  const [configClientPanelModule, setConfigClientPanelModule] = useState({});
  const [module, setModule] = useState({});
  const [paymentModesCopyModule, setPaymentModesCopyModule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      // eslint-disable-next-line no-shadow -- FIXME
      const brandConfig = await dispatch(fetchBrand(selectedBrand));
      const modulesConfig = await dispatch(
        fetchBrandConfigModulesPack(selectedBrand, [
          'Shop',
          'ConfigClientPanel',
          'PaymentModesCopy',
          'SeoCmsMainSite',
          'SeoCmsAddonsSide',
          'SeoCmsDietSide',
          'SeoCmsDishSide',
          'SeoCmsZonesSubpage',
          'SeoCmsMenuSubpage',
          'SeoCmsClientPanelLogin',
          'SeoCmsClientPanelRegister',
          'SeoCmsClientPanelMyAccount',
        ])
      );

      const {
        '@id': idCcpIri,
        '@type': typeCcpIri,
        ...restConfigClientPanelVariables
      } = modulesConfig.configuration.ConfigClientPanel;

      setConfigClientPanelModule(restConfigClientPanelVariables);
      setPaymentModesCopyModule(modulesConfig.configuration.PaymentModesCopy);
      setModule(modulesConfig);
      await dispatch(fetchPaymentTypes(selectedBrand));

      setBrandConfig(prevState => ({
        ...prevState,

        premiumType: brandConfig.premiumType,
        premiumAdditionalStep: brandConfig.premiumAdditionalStep,
        allChangesWithoutCosts: brandConfig.allChangesWithoutCosts,
        allowChangeAddress: brandConfig.allowChangeAddress,
        allowChangeVariant: brandConfig.allowChangeVariant,
        showPricePreview: brandConfig.showPricePreview,
        allowChangeCalorific: brandConfig.allowChangeCalorific,
        allowChangeDeliveryDate: brandConfig.allowChangeDeliveryDate,
        allowChangeDiet: brandConfig.allowChangeDiet,
        allowChangeMultipleDays: brandConfig.allowChangeMultipleDays,
        newCalendarEnabled: brandConfig.newCalendarEnabled,
        showInformation: brandConfig.showInformation,
        showFacebookCodeReference: brandConfig.showFacebookCodeReference,
        showLinkedinCodeReference: brandConfig.showLinkedinCodeReference,
        showMessengerCodeReference: brandConfig.showMessengerCodeReference,
        showRefererCode: brandConfig.showRefererCode,
        showTwitterCodeReference: brandConfig.showTwitterCodeReference,
        showWhatsappCodeReference: brandConfig.showWhatsappCodeReference,
        facebookCodeReferenceLink: brandConfig.facebookCodeReferenceLink
          ? brandConfig.facebookCodeReferenceLink
          : brandConfig.homePageLink,
        linkedinCodeReferenceLink: brandConfig.linkedinCodeReferenceLink
          ? brandConfig.linkedinCodeReferenceLink
          : brandConfig.homePageLink,
        whatsappCodeReferenceLink: brandConfig.whatsappCodeReferenceLink
          ? brandConfig.whatsappCodeReferenceLink
          : brandConfig.homePageLink,
        twitterCodeReferenceLink: brandConfig.twitterCodeReferenceLink
          ? brandConfig.twitterCodeReferenceLink
          : brandConfig.homePageLink,
        messengerCodeReferenceLink: brandConfig.messengerCodeReferenceLink
          ? brandConfig.messengerCodeReferenceLink
          : brandConfig.homePageLink,
        showNutritionalValuesInMenuPreview:
          modulesConfig?.configuration?.ConfigClientPanel
            ?.showNutritionalValuesInMenuPreview || false,
      }));
      setIsLoading(false);
    })();
  }, [dispatch, selectedBrand]);

  const handleChangeDietViewOption = ({ target: { name } }) => {
    setBrandConfig(
      produce(draft => {
        draft.showInformation[name] = !draft.showInformation[name];
      })
    );
  };

  const handleGlycemicIndexChange = ({ target: { name, value } }) => {
    setBrandConfig(
      produce(draft => {
        const isBoolean = ['show'].includes(name);
        const { glycemicIndex } = draft.showInformation;

        glycemicIndex[name] = isBoolean ? !glycemicIndex[name] : value;
      })
    );
  };

  const handleSummaryShowChange = ({ target: { name, value } }) => {
    setBrandConfig(
      produce(draft => {
        const isBoolean = ['show'].includes(name);
        const { summary } = draft.showInformation;

        summary[name] = isBoolean ? !summary[name] : value;
      })
    );
  };

  const handleInputChange = e => {
    setBrandConfig(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const setConfigClientPanelModuleImageByStateName = (fieldName, data) => {
    setConfigClientPanelModule({
      ...configClientPanelModule,
      '@resources': {
        ...(configClientPanelModule?.['@resources'] || {}),
        [data?.['@id']]: { ...data },
      },
      [fieldName]: data?.['@id'] || null,
    });

    if (fieldName === 'dishImageBackground') {
      setChoicesBackground({
        image: null,
        index: null,
      });
    }

    if (fieldName === 'dishImagePlaceholder') {
      setChoicesAlternatePhotoDish({
        image: null,
        index: null,
      });
    }
  };

  const removeConfigClientPanelModuleImageByStateName = fieldName => {
    setConfigClientPanelModule({
      ...configClientPanelModule,
      [fieldName]: null,
    });

    if (fieldName === 'dishImageBackground') {
      setChoicesBackground({
        image: null,
        index: null,
      });
    }

    if (fieldName === 'dishImagePlaceholder') {
      setChoicesAlternatePhotoDish({
        image: null,
        index: null,
      });
    }
  };

  const handleClientCanCanceledDietDay = () => {
    if (configClientPanelModule.allowCancelBags) {
      toast.warning(t('brands.newBrandForm.cancelDayDiet'));
    }
    if (brandConfig.allowChangeDeliveryDate) {
      setConfigClientPanelModule(prevState => ({
        ...prevState,
        allowCancelBags: false,
      }));
    }
  };
  const handleSubmit = async () => {
    if (
      brandConfig.showInformation?.dishCompositionValue?.mode === 'OFF' &&
      brandConfig.showInformation?.dishComposition
    ) {
      toast.error(t('notify.cannotSavePleaseCheckOneOptionDishComposition'));
      return;
    }

    try {
      await put(`/brands/${selectedBrand}`, brandConfig);
      await dispatch(
        updateBrandConfigModulesPack(selectedBrand, {
          ConfigClientPanel: configClientPanelModule,
          PaymentModesCopy: paymentModesCopyModule,
          ItemPaymentTypes: paymentTypes,
          SeoCmsMainSite: module.configuration.SeoCmsMainSite,
          SeoCmsAddonsSide: module.configuration.SeoCmsAddonsSide,
          SeoCmsDietSide: module.configuration.SeoCmsDietSide,
          SeoCmsDishSide: module.configuration.SeoCmsDishSide,
          SeoCmsZonesSubpage: module.configuration.SeoCmsZonesSubpage,
          SeoCmsMenuSubpage: module.configuration.SeoCmsMenuSubpage,
          SeoCmsClientPanelLogin: module.configuration.SeoCmsClientPanelLogin,
          SeoCmsClientPanelRegister:
            module.configuration.SeoCmsClientPanelRegister,
          SeoCmsClientPanelMyAccount:
            module.configuration.SeoCmsClientPanelMyAccount,
        })
      );
      toast.success(t('success.changesSaved'));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- FIXME
    } catch (error) {
      toast.error(t('notify.cannotSave'));
    }
  };

  const handleCheckboxChange = (key, setState = setBrandConfig, ...params) => {
    setState(prevState => ({
      ...prevState,
      [key]: !prevState[key],
      params,
    }));
  };

  return (
    <PanelConfigurationContext.Provider
      value={{
        module,
        isLoading,
        handleChangeDietViewOption,
        handleGlycemicIndexChange,
        handleSummaryShowChange,
        handleInputChange,
        setConfigClientPanelModuleImageByStateName,
        removeConfigClientPanelModuleImageByStateName,
        handleClientCanCanceledDietDay,
        handleSubmit,
        handleCheckboxChange,
        brandConfig,
        configClientPanelModule,
        setConfigClientPanelModule,
        setBrandConfig,
        setModule,
        choicesBackground,
        setChoicesBackground,
        isOpenModal,
        setIsOpenModal,
        choicesAlternatePhotoDish,
        setChoicesAlternatePhotoDish,
        backgroundDialogMode,
        setBackgroundDialogMode,
        backgroundToChoicedArray,
        alternatePhotoToDishArray,
        selectedBrand,
        paymentTypes,
        selectedLanguage,
      }}
    >
      {children}
    </PanelConfigurationContext.Provider>
  );
};

export const usePanelConfigurationContext = () =>
  useContext(PanelConfigurationContext);

export default PanelConfigurationContextProvider;
